import { useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { dashboardPath, loginPath, signupPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { useUiAccess } from "../auth/use-ui-access"
import { useViewerMaybe } from "../auth/use-viewer"

export const RootScreen = () => {
  const { viewer } = useViewerMaybe()
  useUiAccess()
  const navigate = useNavigate()
  useDocumentTitle("Scout Radar — Estate sale alerts, automatically")

  const waitlistRef = useRef<HTMLElement>(null)
  const [email, setEmail] = useState("")
  const [keywords, setKeywords] = useState("")
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle")

  if (viewer) {
    navigate(dashboardPath({}))
    return null
  }

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus("submitting")
    try {
      const res = await fetch("/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, keywords }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error((data as { error?: string }).error ?? "Something went wrong")
      }
    } catch {
      // still show success — don't block the user on transient errors
    }
    setStatus("success")
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <nav className="flex items-center justify-between px-8 py-5 border-b border-stone-200">
        <span
          className="text-xs font-bold tracking-[0.2em] uppercase"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Scout Radar
        </span>
        <div className="flex items-center gap-4">
          <Link
            to={signupPath({})}
            className="text-xs tracking-wide text-stone-700 hover:text-stone-900 px-4 py-2 border border-stone-300 hover:border-stone-500 transition-colors"
          >
            Sign Up
          </Link>
          <Link
            to={loginPath({})}
            className="text-xs tracking-wide text-stone-600 hover:text-stone-900 transition-colors"
          >
            Sign In
          </Link>
        </div>
      </nav>

      <section className="px-8 py-24 max-w-3xl mx-auto text-center">
        <p className="text-xs tracking-[0.2em] uppercase text-stone-400 mb-6">
          Estate sale intelligence
        </p>
        <h1
          className="text-5xl md:text-6xl font-bold leading-tight text-stone-900 mb-6"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Your Estate Sale Auction Streamliner
        </h1>
        <p className="text-lg text-stone-500 leading-relaxed max-w-xl mx-auto mb-10">
          A dashboard to simplify the chaos of Estate Sale Auctions
        </p>
        <button
          onClick={scrollToWaitlist}
          className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-8 py-3 text-sm tracking-wide hover:bg-stone-700 transition-colors"
        >
          Join the Waitlist
          <span aria-hidden>↓</span>
        </button>
      </section>

      <section className="border-t border-stone-200 px-8 py-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-400 mb-12 text-center">
            How it works
          </p>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                n: "01",
                heading: "Tell us what you're hunting",
                body: 'Keywords like "vintage gucci," "danish table," or "pinball machine."',
              },
              {
                n: "02",
                heading: "We scan auctions around the clock",
                body: "Scout Radar checks auction sites every 6 hours, automatically, day and night.",
              },
              {
                n: "03",
                heading: "Only new finds in your inbox",
                body: "You get one digest per cycle with fresh listings only. No repeats, no noise.",
              },
            ].map(({ n, heading, body }) => (
              <div key={n}>
                <p className="text-xs text-stone-300 mb-3 font-mono">{n}</p>
                <h3
                  className="text-lg font-bold text-stone-900 mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {heading}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        ref={waitlistRef}
        id="waitlist"
        className="border-t border-stone-200 bg-stone-100 px-8 py-24"
      >
        <div className="max-w-md mx-auto text-center">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-400 mb-4">Early access</p>
          <h2
            className="text-3xl font-bold text-stone-900 mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Get early access.
          </h2>
          <p className="text-sm text-stone-500 mb-8">
            We&apos;ll let you know when Scout Radar is ready for you.
          </p>

          {status === "success" ? (
            <div className="py-4">
              <p className="text-sm text-stone-900 font-medium">You&apos;re on the list.</p>
              <p className="text-xs text-stone-500 mt-1">We&apos;ll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500"
              />
              <input
                type="text"
                name="keywords"
                placeholder="What are you hunting for? (optional)"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full bg-stone-900 text-stone-50 px-6 py-2.5 text-sm tracking-wide hover:bg-stone-700 transition-colors disabled:opacity-50"
              >
                {status === "submitting" ? "Sending…" : "Notify Me"}
              </button>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-stone-200 px-8 py-8 flex items-center justify-between">
        <span className="text-xs tracking-[0.15em] uppercase text-stone-400">Scout Radar</span>
        <span className="text-xs text-stone-300">© 2026</span>
      </footer>
    </div>
  )
}
